import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { BrandingProvider } from "@/context/BrandingContext";
import { Navbar } from "@/components/Navbar";
import { Toaster } from "@/components/ui/sonner";
import { Loader2 } from "lucide-react";

import Home from "@/pages/Home";
import PropertyDetail from "@/pages/PropertyDetail";
import CreateListing from "@/pages/CreateListing";
import Dashboard from "@/pages/Dashboard";
import Saved from "@/pages/Saved";
import Students from "@/pages/Students";
import RoommateForm from "@/pages/RoommateForm";
import RoommateDetail from "@/pages/RoommateDetail";
import AdminDashboard from "@/pages/AdminDashboard";
import Agents from "@/pages/Agents";
import GetVerified from "@/pages/GetVerified";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Invite from "@/pages/Invite";
import AuthCallback from "@/pages/AuthCallback";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) {
    return (
      <div className="flex h-[60vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#2C3830]" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AdminRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) {
    return (
      <div className="flex h-[60vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#2C3830]" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "admin") return <Navigate to="/dashboard" replace />;
  return children;
}

function Shell() {
  const location = useLocation();

  // Handle Google OAuth callback synchronously before routing
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  const hideNav = ["/login", "/register"].includes(location.pathname);

  return (
    <div className="App min-h-screen">
      {!hideNav && <Navbar />}
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/listing/:id" element={<PropertyDetail />} />
        <Route path="/saved" element={<Saved />} />
        <Route path="/students" element={<Students />} />
        <Route path="/agents" element={<Agents />} />
        <Route path="/agents/:id" element={<Agents />} />
        <Route path="/get-verified" element={<ProtectedRoute><GetVerified /></ProtectedRoute>} />
        <Route path="/roommate/:id" element={<RoommateDetail />} />
        <Route path="/roommates/new" element={<ProtectedRoute><RoommateForm /></ProtectedRoute>} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/invite/:token" element={<Invite />} />
        <Route path="/create" element={<ProtectedRoute><CreateListing /></ProtectedRoute>} />
        <Route path="/edit/:id" element={<ProtectedRoute><CreateListing /></ProtectedRoute>} />
        <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <BrandingProvider>
          <Shell />
          <Toaster position="top-center" richColors />
        </BrandingProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
